// The legacy build is required: the modern build ships syntax webpack 4
// cannot parse, and the legacy one supports older WebViews.
import * as pdfjs from 'pdfjs-dist/legacy/build/pdf.mjs';
// pdf.js's legacy build polyfills only what its own targets lack, not these (built in since Chrome 92 and 98).
// The page has them already through app/index.js's core-js; listed here because the main-thread worker below
// depends on them.
import 'core-js/actual/array/at';
import 'core-js/actual/string/at';
import 'core-js/actual/typed-array/at';
import 'core-js/actual/structured-clone';
import { useState, useEffect } from 'react';
import Bugsnag from '@bugsnag/js';
import { nativeBuiltIns } from '@helpers/nativeBuiltIns';
import { importWorkerModule } from './workerModule';

// The worker is copied into www/ by the CopyPlugin entry in webpack.config.js, so it always matches the
// bundled pdfjs version. On file:// (Cordova iOS) the document's origin is "null", pdfjs treats the local file
// as cross-origin, and WKWebView blocks the resulting module import — so only there, load the version-matched
// worker from a CORS-enabled CDN like the pre-4.x code did.
// Always an absolute URL: when a Worker can't start, pdf.js import()s the same file on the page (its "fake
// worker"), and import() reads a bare 'pdf.worker.min.mjs' as a package name and fails — the preview then
// spins forever.
export const defaultWorkerSrc = (): string => (window.location.protocol === 'file:'
  ? `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`
  : new URL('pdf.worker.min.mjs', document.baseURI).href);

// pdf.js parses the PDF in a Web Worker, which gets only the browser's native built-ins: the page's core-js
// doesn't reach it. Where Array.prototype.at isn't native (Chrome < 92, Firefox < 90, Safari < 15.4 on the web) the
// worker dies while loading — "e.at is not a function", which the browser also reports on the page, to Bugsnag —
// and pdf.js falls back to running it on the page. There, go to the page directly: load the worker module here,
// where core-js filled the gaps, and hand it to pdf.js (globalThis.pdfjsWorker), which then never starts a
// Worker. Every other browser keeps the real worker. (pdfjs-dist 4.10's worker needs nothing else Chrome 92+
// lacks; after an upgrade, check its unguarded built-ins again.)
// The iOS app (file://) loads the CDN worker, which isn't transpiled and needs iOS 16.4 either way.
let loading: ?Promise<void> = null;
let attempts = 0;
let reportedLoadFailure = false;
export const workerReady = (src: string): Promise<void> => {
  if (nativeBuiltIns.arrayAt || window.pdfjsWorker) return Promise.resolve();
  if (!loading) {
    // Absolute: import() reads a bare 'pdf.worker.min.mjs' as a package name. A retry asks for its own URL: the
    // browser keeps a failed module import for its URL and fails it again at once, without fetching it
    const url = new URL(src, document.baseURI).href;
    const current = importWorkerModule(attempts ? `${url}${url.includes('?') ? '&' : '?'}retry=${attempts}` : url)
      .then((worker) => { window.pdfjsWorker = worker; });
    attempts += 1;
    loading = current;
    current.catch((error) => {
      // The next preview tries again
      if (loading === current) loading = null;
      // Once a page load: before, the dying Worker reached Bugsnag through the page; now this is how we'd know
      if (reportedLoadFailure) return;
      reportedLoadFailure = true;
      Bugsnag.notify(error, (event) => {
        event.context = 'PDFPreviewWorkerLoadFailed'; // eslint-disable-line no-param-reassign
      });
    });
  }
  return loading;
};

export type DocumentState = {
  pdfDocument: ?Object,
  // 0..1 while the file downloads, when the server says its size
  progress: ?number,
  failed: boolean,
  // The file arrived but isn't a readable PDF (cut short, or another kind of file): opening it again can't help
  damaged: boolean,
};

const LOADING: DocumentState = { pdfDocument: null, progress: null, failed: false, damaged: false };

// Opens `file` with pdf.js. A null file is still loading (its URL isn't known yet); `attempt` opens it again.
export const usePdfDocument = (file: ?string, attempt: number = 0): DocumentState => {
  // Which file and attempt the state is for: the render that asks for another one, before the effect, gets LOADING
  // rather than the previous file's document (its pages would mount, draw and be thrown away)
  const [opened, setOpened] = useState({ file: null, attempt: 0, state: LOADING });
  const setState = (update: DocumentState | (DocumentState => DocumentState)) => setOpened(current => ({
    file,
    attempt,
    state: typeof update === 'function'
      ? update(current.file === file && current.attempt === attempt ? current.state : LOADING)
      : update,
  }));

  useEffect(() => {
    setState(LOADING);
    if (!file) return undefined;
    pdfjs.GlobalWorkerOptions.workerSrc = defaultWorkerSrc();
    let cancelled = false;
    let loadingTask = null;
    // One chain, so a failed worker load, a getDocument() that throws and a document that fails all end in failed
    workerReady(pdfjs.GlobalWorkerOptions.workerSrc)
      .then(() => {
        if (cancelled) return null;
        loadingTask = pdfjs.getDocument({ url: file, withCredentials: false });
        loadingTask.onProgress = ({ loaded, total }) => {
          if (!cancelled && total) setState(current => ({ ...current, progress: Math.min(1, loaded / total) }));
        };
        return loadingTask.promise;
      })
      .then(
        (pdfDocument) => {
          if (!cancelled && pdfDocument) setState({ pdfDocument, progress: 1, failed: false, damaged: false });
        },
        (error) => {
          const damaged = !!error && error.name === 'InvalidPDFException';
          if (!cancelled) setState({ pdfDocument: null, progress: null, failed: true, damaged });
        },
      );
    return () => {
      // destroy() terminates the per-document worker; without it every open/close of the preview leaks a worker
      // thread and its memory.
      cancelled = true;
      if (loadingTask) loadingTask.destroy();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, attempt]);

  return opened.file === file && opened.attempt === attempt ? opened.state : LOADING;
};

export type PageSize = { width: number, height: number };

// Every page's size in PDF points, rotation applied. Until all are known, each one takes the first page's.
export const usePageSizes = (pdfDocument: ?Object): ?Array<PageSize> => {
  const [sizes, setSizes] = useState(null);

  useEffect(() => {
    setSizes(null);
    if (!pdfDocument) return undefined;
    let cancelled = false;
    const sizeOf = page => {
      const { width, height } = page.getViewport({ scale: 1 });
      return { width, height };
    };
    const count = pdfDocument.numPages;
    pdfDocument.getPage(1).then((first) => {
      if (cancelled) return;
      const firstSize = sizeOf(first);
      setSizes(Array.from({ length: count }, () => firstSize));
      if (count === 1) return;
      Promise.all(Array.from({ length: count - 1 }, (_, i) => pdfDocument.getPage(i + 2).then(sizeOf)))
        .then((rest) => { if (!cancelled) setSizes([firstSize, ...rest]); })
        .catch(() => {});
    }, () => {});
    return () => { cancelled = true; };
  }, [pdfDocument]);

  return sizes;
};

// The page's text, invisible over the canvas, so it can be selected and copied. Sized by the --scale-factor of
// its page: a zoom only changes that.
export const createTextLayer = (page: Object, container: HTMLElement): Object => new pdfjs.TextLayer({
  textContentSource: page.streamTextContent({ includeMarkedContent: true, disableNormalization: true }),
  container,
  viewport: page.getViewport({ scale: 1 }),
});
